import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const VisaApplicationCreatePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const caseId = searchParams.get('case_id') || '';
  const clientId = searchParams.get('client_id') || '';

  const [form, setForm] = useState({
    visa_type: '',
    country: '',
    subclass: '',
    portal_ref: '',
    submitted_at: '',
    biometrics_at: '',
    applicant_name: '',
    passport_number: '',
    case_id: '',
    offer_id: '',
    notes: '',
    status: 'draft',
    visa_product_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [visaProducts, setVisaProducts] = useState([]);
  const [cases, setCases] = useState([]);
  const [offers, setOffers] = useState([]);
  const [toast, setToast] = useState(null);
  const [warnMissingDeadline, setWarnMissingDeadline] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    (async () => {
      try {
        const { data: products, error } = await supabase.from('visa_products').select('id,country,visa_type,name,subclass').order('country').order('name');
        if (error) {
          console.error('Visa products fetch error:', error);
        }
        console.log('Visa products loaded:', products?.length || 0, products);
        setVisaProducts(products || []);
      } catch (e) {
        console.error('Visa products exception:', e);
      }
    })();
  }, []);

  // Fetch available cases for dropdown
  useEffect(() => {
    (async () => {
      try {
        const { data: casesData } = await supabase
          .from('cases')
          .select('id, title, client_id, clients(first_name, last_name)')
          .neq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(100);
        setCases(casesData || []);
      } catch { }
    })();
  }, []);

  // Fetch offers for selected case
  useEffect(() => {
    (async () => {
      const selectedCaseId = form.case_id || caseId;
      if (!selectedCaseId) {
        setOffers([]);
        return;
      }
      try {
        const { data: offersData } = await supabase
          .from('application_offers')
          .select('id, offer_type, tuition_total, expires_at, accepted, uni_applications!application_id(programs(name, universities(name)))')
          .eq('case_id', selectedCaseId)
          .order('created_at', { ascending: false });
        setOffers(offersData || []);
      } catch { setOffers([]); }
    })();
  }, [form.case_id, caseId]);;

  // Auto-populate applicant info from client data
  useEffect(() => {
    (async () => {
      // Try to get client_id from URL or from case
      let targetClientId = clientId;

      // If we have a case_id but no client_id, fetch client from case
      if (!targetClientId && caseId) {
        try {
          const { data: caseData } = await supabase
            .from('cases')
            .select('client_id')
            .eq('id', caseId)
            .single();
          if (caseData?.client_id) {
            targetClientId = caseData.client_id;
          }
        } catch { }
      }

      // Fetch client details
      if (targetClientId) {
        try {
          const { data: client } = await supabase
            .from('clients')
            .select('first_name, last_name, passport_no, primary_email, primary_phone')
            .eq('id', targetClientId)
            .single();

          if (client) {
            const fullName = `${client.first_name || ''} ${client.last_name || ''}`.trim();
            setForm(prev => ({
              ...prev,
              applicant_name: fullName || prev.applicant_name,
              passport_number: client.passport_no || prev.passport_number
            }));
          }
        } catch (e) {
          console.error('Failed to load client for auto-populate:', e);
        }
      }
    })();
  }, [clientId, caseId]);

  const createVisa = async () => {
    try {
      if (!form.visa_type.trim() && !form.country.trim()) {
        setToast({ type: 'error', message: 'Provide visa type or country' });
        return;
      }
      if (!form.biometrics_at && !form.submitted_at) {
        setWarnMissingDeadline(true);
      } else {
        setWarnMissingDeadline(false);
      }
      setSaving(true);
      const payload = {
        case_id: caseId || null,
        client_id: clientId || null,
        visa_product_id: form.visa_product_id || null,
        status: form.submitted_at ? 'submitted' : 'draft',
        submitted_at: form.submitted_at || null,
        biometrics_at: form.biometrics_at || null,
        metadata: {
          visa_type: form.visa_type || null,
          country: form.country || null,
          portal_ref: form.portal_ref || null,
          applicant_name: form.applicant_name || null,
          passport_number: form.passport_number || null,
          offer_id: form.offer_id || null,
          notes: form.notes || null
        }
      };
      const { data: inserted, error } = await supabase.from('visa_applications').insert([payload]).select('*').single();
      if (error) throw error;
      const visaId = inserted?.id;
      // Generate checklist from visa_requirements
      try {
        if (form.visa_product_id) {
          const { data: reqs } = await supabase.from('visa_requirements').select('requirement_key,label,mandatory').eq('visa_product_id', form.visa_product_id).order('sort_order');
          const docs = (reqs || []).map(r => ({ visa_application_id: visaId, requirement_key: r.requirement_key, label: r.label, status: r.mandatory ? 'requested' : 'optional' }));
          if (docs.length) await supabase.from('visa_app_documents').insert(docs);
        }
      } catch { }
      try { await supabase.from('case_notes').insert([{ case_id: caseId, content: `Visa application created: ${payload.metadata.visa_type || ''} • ${payload.metadata.country || ''}` }]); } catch { }
      setToast({ type: 'success', message: 'Visa application created' });
      if (caseId) navigate(`/admin/cases/${caseId}`);
      else navigate('/admin/visa_apps');
    } catch (e) {
      setToast({ type: 'error', message: e.message || 'Failed to create visa application' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded shadow-lg border ${toast.type === 'success' ? 'bg-green-900/90 border-green-700 text-green-100' : 'bg-red-900/90 border-red-700 text-red-100'
          }`}>
          {toast.message}
        </div>
      )}

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">New Visa Application</h1>
            <p className="text-gray-400 text-sm">Start a new visa application process</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Visa Details */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50">
                <h3 className="font-bold text-white text-sm uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Visa Selection
                </h3>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Visa Product *</label>
                  <select
                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    value={form.visa_product_id}
                    onChange={e => {
                      const prod = visaProducts.find(p => p.id === e.target.value);
                      setForm({
                        ...form,
                        visa_product_id: e.target.value,
                        country: prod ? prod.country : '',
                        visa_type: prod ? prod.visa_type : '',
                        subclass: prod ? prod.subclass : ''
                      });
                    }}
                  >
                    <option value="">Select Visa Product</option>
                    {visaProducts.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.country} - {p.visa_type} {p.subclass ? `(${p.subclass})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Country</label>
                    <input
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-gray-400 bg-opacity-50 cursor-not-allowed"
                      value={form.country}
                      readOnly
                      placeholder="Auto-filled"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Visa Type</label>
                    <input
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-gray-400 bg-opacity-50 cursor-not-allowed"
                      value={form.visa_type}
                      readOnly
                      placeholder="Auto-filled"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Subclass</label>
                    <input
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-gray-400 bg-opacity-50 cursor-not-allowed"
                      value={form.subclass || ''}
                      readOnly
                      placeholder="Auto-filled"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Applicant Info */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50">
                <h3 className="font-bold text-white text-sm uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Applicant Information
                </h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Applicant Name *</label>
                    <input
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Full Name"
                      value={form.applicant_name}
                      onChange={e => setForm({ ...form, applicant_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Passport Number</label>
                    <input
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="e.g. A12345678"
                      value={form.passport_number}
                      onChange={e => setForm({ ...form, passport_number: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Link to Case (Optional)</label>
                    <select
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      value={form.case_id || caseId}
                      onChange={e => {
                        const selectedCaseId = e.target.value;
                        setForm({ ...form, case_id: selectedCaseId });
                        // Auto-populate client from selected case
                        if (selectedCaseId) {
                          const selectedCase = cases.find(c => c.id === selectedCaseId);
                          if (selectedCase?.clients) {
                            const fullName = `${selectedCase.clients.first_name || ''} ${selectedCase.clients.last_name || ''}`.trim();
                            if (fullName) setForm(prev => ({ ...prev, case_id: selectedCaseId, applicant_name: fullName }));
                          }
                        }
                      }}
                      disabled={!!caseId}
                    >
                      <option value="">No case linked</option>
                      {cases.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.title || c.id.slice(0, 8)} {c.clients ? `• ${c.clients.first_name || ''} ${c.clients.last_name || ''}`.trim() : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Link to Offer (Optional)</label>
                    <select
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      value={form.offer_id}
                      onChange={e => setForm({ ...form, offer_id: e.target.value })}
                      disabled={offers.length === 0}
                    >
                      <option value="">{offers.length === 0 ? 'No offers available' : 'No offer linked'}</option>
                      {offers.map(o => (
                        <option key={o.id} value={o.id}>
                          {o.uni_applications?.programs?.universities?.name || 'Uni'} • {o.uni_applications?.programs?.name || 'Program'} ({o.offer_type || 'offer'}) {o.accepted ? '✓' : ''}
                        </option>
                      ))}
                    </select>
                    {offers.length === 0 && (form.case_id || caseId) && (
                      <p className="text-xs text-gray-500 mt-1">No offers found for this case</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Submission Date</label>
                    <input
                      type="date"
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      value={form.submitted_at}
                      onChange={e => setForm({ ...form, submitted_at: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Biometrics / Appointment</label>
                    <input
                      type="date"
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      value={form.biometrics_at}
                      onChange={e => setForm({ ...form, biometrics_at: e.target.value })}
                    />
                    {warnMissingDeadline && !form.biometrics_at && !form.submitted_at && (
                      <div className="mt-2 text-xs text-yellow-400">
                        No submission or biometrics date set — this visa app will not trigger Control Room deadline alerts.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50">
                <h3 className="font-bold text-white text-sm uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Notes
                </h3>
              </div>
              <div className="p-6">
                <textarea
                  rows={4}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Additional notes or requirements..."
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Summary & Actions */}
          <div className="space-y-6">
            <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden">
              <div className="p-6">
                <h3 className="text-lg font-bold text-white mb-4">Summary</h3>

                <div className="space-y-4 text-sm">
                  <div className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-400">Product</span>
                    <span className="text-white font-medium text-right truncate ml-4">
                      {visaProducts.find(p => p.id === form.visa_product_id)?.visa_type || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-400">Country</span>
                    <span className="text-white font-medium text-right">{form.country || '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-400">Applicant</span>
                    <span className="text-white font-medium text-right truncate ml-4">{form.applicant_name || '-'}</span>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Status</label>
                    <span className="px-2 py-1 rounded text-xs font-bold bg-blue-900/30 text-blue-400 border border-blue-800 uppercase">
                      {form.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>

                <button
                  onClick={createVisa}
                  disabled={saving}
                  className="w-full mt-6 px-6 py-3 rounded-lg text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-900/20 transition-all transform active:scale-95 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Application'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaApplicationCreatePage;
